<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
    show: {
        type: Boolean,
        default: true
    },

    title: {
        type: String,
        default: 'Teilnahmebedingungen & Datenschutz'
    },
    description: {
        type: String,
        default: 'Bevor Sie am Adventskalender teilnehmen, benötigen wir Ihre Zustimmung zu den Teilnahmebedingungen und zur Verarbeitung persönlicher Daten.'
    },

    privacyUrl: {
        type: String,
        default: 'https://hkcmanagement.de/verlosungdatenschutz'
    },
    privacyLinkLabel: {
        type: String,
        default: 'Zu den Datenschutzinformationen'
    },
    calendarUrl: {
        type: String,
        default: 'https://hkcmanagement.de/verlosung'
    },
    calendarLinkLabel: {
        type: String,
        default: 'Zu den Teilnahmebedingungen'
    },

    dataConsentLabel: {
        type: String,
        default: 'Ich habe die Teilnahmebedingungen und Datenschutzhinweise gelesen und akzeptiere sie.'
    },
    nameConsentLabel: {
        type: String,
        default: 'Optional: Ich bin einverstanden, dass mein Name im Gewinnfall pseudonymisiert veröffentlicht werden darf.'
    },

    requireNameConsent: {
        type: Boolean,
        default: true
    },

    acceptLabel: {
        type: String,
        default: 'Einverstanden & teilnehmen'
    },
    declineLabel: {
        type: String,
        default: 'Abbrechen'
    }
});

const emits = defineEmits(['update:show', 'accept', 'decline']);

const dataConsent = ref(false);
const nameConsent = ref(false);

watch(
    () => props.show,
    (val) => {
        if (val) {
            dataConsent.value = false;
            nameConsent.value = false;
        }
    }
);

const canSubmit = computed(() => {
    if (!props.requireNameConsent) {
        return dataConsent.value;
    }

    return dataConsent.value;
});

const handleClose = () => {
    emits('update:show', false);
};

const handleDecline = () => {
    handleClose();
    emits('decline');
};

const handleAccept = () => {
    if (!canSubmit.value) return;

    handleClose();
    emits('accept', {
        dataConsent: dataConsent.value,
        nameConsent: nameConsent.value
    });
};
</script>

<template>
    <transition name="fade">
        <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div
                class="relative w-full max-w-2xl mx-4 rounded-2xl shadow-2xl border border-white/10 bg-gradient-to-b from-slate-900/95 via-slate-900/90 to-slate-950/95 text-slate-100 max-h-[90vh] overflow-y-auto"
            >
                <div class="h-1.5 rounded-t-2xl bg-gradient-to-r from-amber-400 via-blue-500 to-amber-300"></div>

                <div class="p-6 sm:p-7 space-y-5">
                    <div class="flex flex-row items-start justify-between gap-4">
                        <div class="space-y-1.5">
                            <h2 class="text-lg sm:text-xl font-semibold tracking-tight flex items-center gap-4">
                                <span class="flex h-12 w-12 items-center justify-center rounded-full bg-amber-400/10 border border-amber-400/30">
                                    <span class="i fi fi-rr-lock text-xl text-amber-300 flex items-center"></span>
                                </span>
                                <span>{{ title }}</span>
                            </h2>
                            <p class="text-sm text-slate-300 leading-relaxed">
                                {{ description }}
                            </p>
                        </div>

                        <button
                            type="button"
                            class="shrink-0 flex items-center h-8 w-8 justify-center rounded-full border border-white/10 text-slate-300 hover:text-white hover:border-white/30 hover:bg-white/5 transition-colors"
                            aria-label="Schließen"
                            @click="handleDecline"
                        >
                            <span class="fi fi-rr-cross-small flex items-center justify-center text-base"></span>
                        </button>
                    </div>

                    <div class="flex flex-col items-center justify-between gap-3 text-xs sm:text-sm">
                        <p class="text-slate-400">Alle Details finden Sie auf den folgenden Seiten:</p>
                        <a
                            v-if="calendarUrl"
                            :href="calendarUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="inline-flex items-center gap-2 rounded-full border border-blue/70 bg-blue/40 px-3 py-1.5 text-xs font-medium text-blue-100 hover:border-blue-400 hover:bg-blue-500/20 transition-colors"
                        >
                            <span class="fi fi-rr-external-link text-[0.65rem]"></span>
                            <span>{{ calendarLinkLabel }}</span>
                        </a>
                        <a
                            v-if="privacyUrl"
                            :href="privacyUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="inline-flex items-center gap-2 rounded-full border border-blue/70 bg-blue/40 px-3 py-1.5 text-xs font-medium text-blue-100 hover:border-blue-400 hover:bg-blue-500/20 transition-colors"
                        >
                            <span class="fi fi-rr-external-link text-[0.65rem]"></span>
                            <span>{{ privacyLinkLabel }}</span>
                        </a>
                    </div>

                    <div class="space-y-3 rounded-xl bg-slate-900/60 border border-white/5 px-4 py-3">
                        <label class="flex items-start gap-3 cursor-pointer">
                            <input v-model="dataConsent" type="checkbox" class="mt-1 h-4 w-4 rounded border-slate-500 bg-slate-900 text-blue-500 focus:ring-blue-500" />
                            <span class="text-xs sm:text-sm text-slate-200 leading-relaxed">
                                {{ dataConsentLabel }}
                            </span>
                        </label>

                        <label v-if="requireNameConsent" class="flex items-start gap-3 cursor-pointer">
                            <input v-model="nameConsent" type="checkbox" class="mt-1 h-4 w-4 rounded border-slate-500 bg-slate-900 text-blue-500 focus:ring-blue-500" />
                            <span class="text-xs sm:text-sm text-slate-200 leading-relaxed">
                                {{ nameConsentLabel }}
                            </span>
                        </label>

                        <p v-if="!canSubmit" class="text-[0.7rem] sm:text-xs text-amber-300/90 mt-1">Bitte bestätigen Sie das erforderliche Feld, um fortzufahren.</p>
                    </div>

                    <div class="flex flex-col sm:flex-row sm:justify-end gap-3 pt-1">
                        <button
                            type="button"
                            class="inline-flex justify-center items-center rounded-full border border-slate-600/80 px-4 py-2 text-xs sm:text-sm font-medium text-slate-200 hover:bg-slate-800/80 hover:border-slate-400/80 transition-colors"
                            @click="handleDecline"
                        >
                            {{ declineLabel }}
                        </button>
                        <button
                            type="button"
                            :disabled="!canSubmit"
                            class="inline-flex justify-center items-center rounded-full bg-blue px-4 py-2 text-xs sm:text-sm font-semibold text-white shadow-lg shadow-blue-900/40 hover:bg-blue disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            @click="handleAccept"
                        >
                            {{ acceptLabel }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease-out;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
